<template>
  <div>
    <div v-if="inProgress">
      {{ $t('Loading...') }}
    </div>
    <div
      v-else
      ref="contentToPrint"
    >
      <template v-if="isPermitted('multimedia')">
        <template v-if="showMultiMedia">
          <div v-if="mediaCheckInProgress">
            {{ $t('Searching media file...') }}
          </div>
          <div v-if="mediaExists && mediaType === '.mp3'">
            <div
              v-if="audioLoading"
              class="mb-1"
            >
              <b-spinner
                small
                class="mr-1"
              />
              {{ $t('Loading...') }}
            </div>
            <audio
              v-if="audioBlobUrl"
              ref="audioPlayer"
              :src="audioBlobUrl"
              controls
              preload="auto"
            >
              Your browser does not support the audio tag.
            </audio>
            <div class="d-flex align-items-center mt-1 flex-wrap">
              <b-button
                size="sm"
                variant="outline-secondary"
                class="mr-1 mb-1"
                @click="seekAudio(-10)"
              >
                &laquo; 10s
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                class="mr-1 mb-1"
                @click="seekAudio(10)"
              >
                10s &raquo;
              </b-button>
              <label class="mr-1 mb-1 mb-0">{{ $t('Speed') }}:</label>
              <b-button
                v-for="rate in playbackRates"
                :key="rate"
                size="sm"
                :variant="playbackRate === rate ? 'primary' : 'outline-primary'"
                class="mr-1 mb-1"
                @click="setPlaybackRate(rate)"
              >
                {{ rate }}x
              </b-button>
            </div>
          </div>
          <a
            v-else-if="mediaExists"
            :href="mediaUrl"
            target="_blank"
          >
            {{ $t('Download') }}
          </a>
          <div
            v-if="!mediaCheckInProgress && !mediaExists"
            class="text-danger"
          >
            {{ $t('No media') }}
          </div>
        </template>
        <template v-else>
          <strong>{{ $t('Survey Id') }}: {{ orjref }}</strong>
        </template>
      </template>

      <b-row>
        <b-col>
          <p
            v-if="allowSurveyAppeal"
            class="mt-2 mb-2"
          >
            <b-link
              v-if="appeal && config && config.appealOnSurvey && allowAppeal"
              @click="doAppeal({ questionText: $t('Appeal On Survey') })"
            >{{
              $t('Appeal On Survey') }}</b-link>
          </p>
          <div
            v-for="(item, index) in surveyData"
            :key="index"
            class="mt-1"
          >
            <label>{{ item.questionText }}</label>
            <b-link
              v-if="isPermitted('seeAppeals') && appeal && allowAppeal && config && config.appealQuestionNames && config.appealQuestionNames.length && config.appealQuestionNames.includes(item.questionName) && allowAppealOnQuestion(item.questionName)"
              class="ml-1"
              @click="doAppeal(item)"
            >{{ $t('Appeal') }}</b-link>
            <div v-if="!item.responseTextUnMasked">
              {{ item.responseText || '-' }}
              <b-spinner
                v-if="item.questionName.indexOf('gdpr_') > -1 && gdpr_inprogress"
                small
                class="ml-1"
                label="Small Spinner"
              />
            </div>
            <div v-if="item.responseTextUnMasked">
              {{ item.responseTextUnMasked }}
            </div>
          </div>
        </b-col>
        <b-col
          v-if="appeal && selectedForAppeal"
          style="border-left:#d0d0d0 1px dashed"
        >
          <template v-if="isInPeriod && canAppeal">
            <p>{{ selectedForAppeal.questionText }}</p>

            <form-appeal
              ref="appeal_form"
              v-model="selectedForAppeal"
              :appeal-reason-options="config.appealReasons"
              :projectid="projectid"
              :orjref="orjref"
              @loading="setAppealInProgress"
              @submit="submitted"
            />

            <b-button
              :disabled="appealInProgress"
              variant="primary"
              @click="saveAppeal()"
            >
              <b-spinner
                v-if="appealInProgress"
                small
                class="mr-50"
                :label="$t('Saving...')"
              />
              {{ $t('Save') }}
            </b-button>
            <b-button
              class="ml-1"
              :disabled="appealInProgress"
              variant="outline-primary"
              @click="selectedForAppeal = null"
            >{{ $t('Cancel')
            }}</b-button>
          </template>
          <template v-else-if="!isInPeriod && canAppeal">
            <p>{{ $t('Appeals are not allowed at this time.') }}</p>
            <b-button
              variant="outline-primary"
              @click="selectedForAppeal=null"
            >
              {{ $t('Go back') }}
            </b-button>
          </template>
          <template v-else-if="canAppeal === false">
            <p>{{ $t('You have reached the maximum allowed appeals for this period.') }}</p>
          </template>
        </b-col>
        <b-col
          v-if="(showAppeal || appeals) && appeal && !selectedForAppeal"
          style="border-left:#d0d0d0 1px dashed"
        >
          <p><strong>{{ $t('Appeal History') }}</strong></p>

          <ul
            v-if="appeals && appeals.length"
            class="small"
          >
            <li
              v-for="(item, index) in appeals"
              :key="index"
              class="mt-1"
            >
              <span>
                <strong v-if="item.questionName">{{ getQuestionText(item.questionName) }}</strong>
                <strong v-else>{{ $t('Appeal On Survey') }}</strong> - <small>{{ formatDate(item.appealDate) }}</small>
                <small>
                  <span v-if="item.appealReason"><br>{{ $t('Appeal Reason') }} : {{ $t(item.appealReason) }}</span>
                  <br>{{ $t('Description') }} : {{ $t(item.description) }}
                  <span v-if="item.resultDescription && !appealManagement"><br>{{ $t('Appeal Result') }} : {{ $t(item.resultDescription)
                  }}</span>
                  <p><b-badge :variant="item.status === 'Open Appeal' ? 'warning' : item.status === 'Accepted' ? 'success' : 'danger'">{{ $t(item.status) }}</b-badge></p>
                </small>
              </span>

              <!-- appeal management-->
              <div v-if="appealManagement && item.status === 'Open Appeal'">
                <b-form-group
                  label-for="resultDescription"
                  :label="$t('Appeal Result')"
                >
                  <b-form-textarea
                    id="resultDescription"
                    v-model="item.resultDescription"
                    :placeholder="$t('Enter appeal result')"
                    rows="3"
                  />
                </b-form-group>
                <b-button
                  :disabled="isAppealActionInProgress(item)"
                  variant="success"
                  @click="accepted(item)"
                >
                  <b-spinner
                    v-if="isAppealActionInProgress(item, 'accept')"
                    small
                    class="mr-50"
                    :label="$t('Saving...')"
                  />
                  {{ $t('Accept') }}
                </b-button>
                <b-button
                  class="ml-1"
                  :disabled="isAppealActionInProgress(item)"
                  variant="danger"
                  @click="rejected(item)"
                >
                  <b-spinner
                    v-if="isAppealActionInProgress(item, 'reject')"
                    small
                    class="mr-50"
                    :label="$t('Saving...')"
                  />
                  {{ $t('Reject') }}
                </b-button>
              </div>

            </li>
          </ul>
          <p v-else>
            {{ $t('No records') }}
          </p>
        </b-col>
      </b-row>

    </div>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import axios from 'axios'
import html2canvas from 'html2canvas'
import JsPDF from 'jspdf'

import {
  BSpinner,
  BLink,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormTextarea,
  BBadge,
} from 'bootstrap-vue'
import { getUserData, isPermitted } from '@/auth/utils'
import dayjs from 'dayjs'

import FormAppeal from './forms/FormAppeal.vue'

export default {
  components: {
    BSpinner,
    BLink,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormTextarea,
    FormAppeal,
    BBadge,
  },
  props: {
    projectid: {
      type: [Number, String],
      required: true,
    },
    orjref: {
      type: [Number, String],
      required: true,
    },
    appeal: {
      type: Boolean,
      default: false,
    },
    appealManagement: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      surveyData: null,
      mediaExists: false,
      mediaUrl: null,
      mediaType: 'mp3',
      inProgress: true,
      gdpr_inprogress: false,
      mediaCheckInProgress: true,
      config: null,
      allowAppeal: false,
      selectedForAppeal: null,
      appeals: null,
      showAppeal: false,
      appealInProgress: false,
      canAppeal: null,
      isInPeriod: null,
      appealActionInProgress: null,
      showMultiMedia: false,
      playbackRate: 1,
      playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
      audioBlobUrl: null,
      audioLoading: false,
    }
  },
  computed: {
    allowSurveyAppeal() {
      if (this.appeals && this.appeals.find(q => !q.questionName)) {
        return false
      }
      return true
    },
  },
  watch: {
    async orjref() {
      this.loadSurveyData(this.projectid, this.orjref)
      await this.checkPeriod()
      await this.checkCanAppeal()
    },
  },
  async created() {
    this.userData = getUserData()
    this.loadSurveyData(this.projectid, this.orjref)
    await this.checkPeriod()
    await this.checkCanAppeal()
  },
  beforeDestroy() {
    this.revokeAudioBlob()
  },
  methods: {
    setAppealInProgress(inProgress) {
      this.appealInProgress = inProgress
      this.$emit('appeal-loading', inProgress)
    },
    async checkPeriod() {
      try {
        const response = await useJwt.axiosIns.get(`project/appeals/checkPeriod/${this.projectid}`)
        if (response.data) {
          this.isInPeriod = true
        } else {
          this.isInPeriod = false
        }
      } catch (e) {
        console.log(e)
        this.isInPeriod = null
      }
    },
    async checkCanAppeal() {
      try {
        const response = await useJwt.axiosIns.get(`project/appeals/canAppeal/${this.projectid}/${this.orjref}`)
        if (response.data) {
          this.canAppeal = true
        } else {
          this.canAppeal = false
        }
      } catch (e) {
        console.log(e)
        this.canAppeal = null
      }
    },
    revokeAudioBlob() {
      if (this.audioBlobUrl) {
        URL.revokeObjectURL(this.audioBlobUrl)
        this.audioBlobUrl = null
      }
    },
    loadAudioBlob(url) {
      this.revokeAudioBlob()
      this.audioLoading = true
      axios.get(url, { responseType: 'blob' })
        .then(response => {
          this.audioBlobUrl = URL.createObjectURL(response.data)
          this.audioLoading = false
          this.$nextTick(() => {
            const audio = this.$refs.audioPlayer
            if (audio) {
              audio.playbackRate = this.playbackRate
            }
          })
        })
        .catch(e => {
          this.audioLoading = false
          console.log(e)
        })
    },
    seekAudio(seconds) {
      const audio = this.$refs.audioPlayer
      if (!audio) return
      const target = audio.currentTime + seconds
      const duration = Number.isFinite(audio.duration) ? audio.duration : null
      if (target < 0) {
        audio.currentTime = 0
      } else if (duration !== null && target > duration) {
        audio.currentTime = duration
      } else {
        audio.currentTime = target
      }
    },
    setPlaybackRate(rate) {
      this.playbackRate = rate
      const audio = this.$refs.audioPlayer
      if (audio) {
        audio.playbackRate = rate
      }
    },
    async exportToPDF() {
      const content = this.$refs.contentToPrint

      // Define margin
      const margin = 10 // 10mm margin on all sides
      const pdf = new JsPDF('p', 'mm', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth() - 2 * margin

      // Use html2canvas to convert the content to an image
      const canvas = await html2canvas(content)

      // Calculate the required height for the content
      const imgHeight = (canvas.height * pageWidth) / canvas.width

      // Adjust the PDF height to fit all content in one page
      pdf.internal.pageSize.height = imgHeight + 2 * margin

      // Add the image to the PDF
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, margin, pageWidth, imgHeight)

      // Save the PDF
      pdf.save(`${this.orjref}.pdf`)
    },
    formatDate(date) {
      return dayjs(date).format('DD.MM.YYYY')
    },
    getQuestionText(questionName) {
      const q = this.surveyData.find(x => x.questionName === questionName)
      if (q) {
        return q.questionText
      }
      return questionName
    },
    allowAppealOnQuestion(questionName) {
      if (this.appeals && this.appeals.find(q => q.questionName === questionName)) {
        return false
      }
      return true
    },
    isPermitted(code) {
      // skip check if pdf export
      if (!this.userData) {
        return false
      }
      return isPermitted(this.userData.role, code, this.userData.userGroups)
    },
    loadSurveyData(projectId, orjRef) {
      this.inProgress = true

      useJwt.axiosIns.get(`hotAlert/surveyData/${projectId}/${orjRef}`)
        .then(response => {
          if (response.data) {
            this.surveyData = response.data
            if (this.surveyData) {
              if (this.appeal) {
                this.getProjectMultimediaConfig(projectId)
                this.getProjectAppealConfig(projectId)
                this.loadAppeals()
              }
              const gdprQuestions = this.surveyData.filter(x => x.questionName.indexOf('gdpr_') > -1 && x.responseText)
              if (gdprQuestions.length) {
                let endpoint = ''
                useJwt.axiosIns.get(`hotAlert/gdpr/endpoint/${projectId}`).then(res => {
                  endpoint = res.data
                  if (endpoint) {
                    this.gdpr_inprogress = true
                    const queryObject = {
                      ProjectId: projectId,
                      OrjRef: orjRef,
                      QuestionNames: gdprQuestions.map(q => q.questionName),
                    }
                    axios.post(endpoint, queryObject).then(resp => {
                      if (resp.data) {
                        resp.data.forEach(element => {
                          this.surveyData.find(s => s.questionName === element.questionName).responseTextUnMasked = element.unMaskedValue
                        })
                      }
                      this.gdpr_inprogress = false
                    }).catch(e => {
                      this.gdpr_inprogress = false
                      console.log(e)
                    })
                  }
                }).catch(e => {
                  console.log(e)
                })
              }
            }
          }
          this.inProgress = false
        })
        .catch(e => {
          this.inProgress = false
          console.log(e)
        })

      if (this.isPermitted('multimedia')) {
        this.mediaCheckInProgress = true
        this.mediaExists = false
        this.mediaUrl = ''
        useJwt.axiosIns.get(`hotAlert/surveyData/checkMediaEnc/${projectId}/${orjRef}`)
          .then(response => {
            this.mediaCheckInProgress = false

            if (response.data) {
              this.mediaExists = true
              if (this.mediaExists) {
                this.mediaType = null
                let encd = response.data
                if (encd && encd[0] === '*') {
                  encd = encd.substring(1)
                  this.mediaType = '.mp3'
                }

                if (this.mediaType === '.mp3') {
                  this.mediaUrl = `https://sendit.ipsos.com.tr/publicapi/cxplus/getmediaEnc?key=${encd}`
                  this.loadAudioBlob(this.mediaUrl)
                } else {
                  this.mediaUrl = `https://sendit.ipsos.com.tr/publicapi/cxplus/downloadMediaEnc?key=${encd}`
                }
              }
            }
          })
          .catch(e => {
            console.log(e)
          })
      }
    },
    getProjectMultimediaConfig(projectId) {
      this.inProgress = true
      this.showMultiMedia = false

      useJwt.axiosIns.get(`hotalert/${projectId}/multimedia`)
        .then(response => {
          this.showMultiMedia = response.data
          this.inProgress = false
        })
        .catch(e => {
          this.inProgress = false
          console.log(e)
        })
    },
    getProjectAppealConfig(projectId) {
      this.inProgress = true
      this.showAppeal = false

      useJwt.axiosIns.get(`project/config/${projectId}`)
        .then(response => {
          if (response.data) {
            this.config = response.data
            if (this.config.appealOnSurvey || (this.config.appealQuestionNames && this.config.appealQuestionNames.length)) {
              this.$emit('appealExists')
              this.showAppeal = true
            }
            const periodQuestion = this.surveyData.find(s => s.questionName === 'donem')
            console.log('this.config.appealPeriods', this.config.appealPeriods)
            console.log('this.surveyData', this.surveyData)
            console.log('periodQuestion', periodQuestion)
            if (periodQuestion != null && this.config.appealPeriods && this.config.appealPeriods.length && this.config.appealPeriods.includes(Number(periodQuestion.responseText))) {
              this.allowAppeal = true
            } else {
              this.allowAppeal = false
            }
            console.log('this.allowAppeal', this.allowAppeal)
          }
          this.inProgress = false
        })
        .catch(e => {
          this.inProgress = false
          console.log(e)
        })
    },
    isAppealActionInProgress(item, action = null) {
      return this.appealActionInProgress
        && this.appealActionInProgress.appealId === item.id
        && (!action || this.appealActionInProgress.action === action)
    },
    accepted(item) {
      this.appealActionInProgress = { appealId: item.id, action: 'accept' }
      const patchItem = {
        id: item.id,
        appealBy: item.appealBy,
        projectId: item.projectId,
        orjRef: item.orjRef,
        questionName: item.questionName,
        appealReason: item.appealReason,
        description: item.description,
        resultDescription: item.resultDescription,
        status: 'Accepted',
      }
      useJwt.axiosIns.patch('project/appeal', patchItem).then(() => {
        this.$emit('reload')
        this.appeals.find(a => a.id === item.id).status = 'Accepted'
      }).finally(() => {
        this.appealActionInProgress = null
      })
    },
    rejected(item) {
      this.appealActionInProgress = { appealId: item.id, action: 'reject' }
      useJwt.axiosIns.patch('project/appeal', {
        id: item.id,
        appealBy: item.appealBy,
        projectId: item.projectId,
        orjRef: item.orjRef,
        questionName: item.questionName,
        appealReason: item.appealReason,
        description: item.description,
        resultDescription: item.resultDescription,
        status: 'Rejected',
      }).then(() => {
        this.$emit('reload')
        this.appeals.find(a => a.id === item.id).status = 'Rejected'
      }).finally(() => {
        this.appealActionInProgress = null
      })
    },

    loadAppeals() {
      this.inProgress = true

      useJwt.axiosIns.get(`project/appeals/${this.projectid}/${this.orjref}`)
        .then(response => {
          if (response.data) {
            this.appeals = response.data
            if (this.appeals.length > 0) {
              this.$emit('appealExists')
            }
          }
          this.inProgress = false
        })
        .catch(e => {
          this.inProgress = false
          console.log(e)
        })
    },
    doAppeal(item) {
      this.selectedForAppeal = {
        projectid: this.projectid,
        orjref: this.orjref,
        questionName: item.questionName ? item.questionName : '',
        questionText: item.questionText,
      }
    },
    saveAppeal() {
      this.$refs.appeal_form.validationForm()
    },
    submitted() {
      this.setAppealInProgress(false)
      this.selectedForAppeal = null
      this.$swal({
        icon: 'success',
        title: this.$t('Saved!'),
        text: this.$t('Appeal saved successfuly.'),
        customClass: {
          confirmButton: 'btn btn-success',
        },
      })
      this.loadAppeals()
    },
  },
}
</script>
